import { useState, useMemo, useEffect } from 'react';

import { Button, ContainerSizes, PageContainer, Spinfinity } from '@repo/ui';
import { formatToDollarsString, copyToClipboard } from 'helpers';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useDiscounts, { Discount } from 'services/discounts/useDiscounts';

import { Card, Header, Icon, Typography } from 'components';
import { ReferralWidget } from '../Home/HomeContent/ReferralWidget/ReferralWidget';

import styles from './Referrals.module.css';
import { useSelector } from 'react-redux';
import { getReferralCredit } from 'store/account';
import RedeemCreditModal from 'views/Account/RedeemCreditModal/RedeemCreditModal';

// CreditItem Component
interface CreditItemProps {
  discount: Discount;
}

const CreditItem: React.FC<CreditItemProps> = ({ discount }) => {
  const [isCopied, setIsCopied] = useState(false);
  const isUsed = !!discount.used_order_id;

  const handleCopyCode = () => {
    copyToClipboard(discount.discount_code);
    setIsCopied(true);
  };

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  return (
    <div className={styles.creditItem}>
      <div className='flex gap-3 align-items-center pl-2'>
        <Typography variant='body-s' className={styles.creditCode}>
          {discount.discount_code}
        </Typography>
        <Typography variant='body-s'>
          {formatToDollarsString(discount.amount / 100)}
        </Typography>
        {isUsed && (
          <Typography variant='body-s' className={styles.usedStatus}>
            Used
          </Typography>
        )}
      </div>
      <div className={styles.creditItemRight}>
        <Button
          onClick={handleCopyCode}
          size='small'
          disabled={isUsed}
          className={styles.copyButton}
        >
          {isCopied ? 'Copied' : 'Copy code'}
          <Icon name={isCopied ? 'check' : 'copy'} size='s' />
        </Button>
      </div>
    </div>
  );
};

export const Referrals = () => {
  const {
    data: discounts = [],
    isLoading,
    refetch: refetchDiscounts,
  } = useDiscounts();
  const referralCredit = useSelector(getReferralCredit);
  const { trackEvent } = useAnalytics();

  const [showRedeemCreditModal, setShowRedeemCreditModal] = useState(false);

  const toggleRedeemCreditModal = () => setShowRedeemCreditModal(prev => !prev);

  useEffect(() => {
    trackEvent('Invite Friend Viewed');
    trackEvent('Credits Viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort discounts by created_at descending
  const sortedDiscounts = useMemo(() => {
    return [...discounts].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [discounts]);

  return (
    <PageContainer size={ContainerSizes.MD}>
      <RedeemCreditModal
        showModal={showRedeemCreditModal}
        toggle={toggleRedeemCreditModal}
        onRedeemSuccess={refetchDiscounts}
      />
      <Header type='simple' title={`Refer a Friend`} />

      {/* Modern ReferralWidget */}
      <ReferralWidget />

      {/* Referral Credits Section */}
      <div className='p-2'>
        <Card variant='wrapper' className={styles.referralCreditsContainer}>
          {isLoading ? (
            <Spinfinity />
          ) : (
            <div className={styles.referralCreditsContent}>
              <div className={styles.referralCreditsHeader}>
                <div className={styles.referralCreditsIcon}>
                  <Icon
                    name='bankNote'
                    className={styles.referralCreditsIcon}
                  />
                </div>
                <div className={styles.referralCreditsHeaderContent}>
                  <div className='flex justify-between'>
                    <Typography variant='heading-l'>My credits</Typography>
                    <Button
                      onClick={toggleRedeemCreditModal}
                      size='small'
                      className={styles.redeemButton}
                      disabled={referralCredit <= 0}
                    >
                      Redeem Credit
                    </Button>
                  </div>
                  <Typography variant='label'>
                    {referralCredit > 0 ? (
                      <>
                        You've earned {formatToDollarsString(referralCredit)}.
                        Click "Redeem Credit" to generate a discount code you
                        can use in the Tiny Health store
                      </>
                    ) : (
                      <>
                        Share your referral code to earn Tiny Health store
                        credit!
                      </>
                    )}
                  </Typography>
                </div>
              </div>
              {sortedDiscounts.length > 0 && (
                <div className='w-100'>
                  {/* Individual Credits List */}
                  <div className={styles.creditsList}>
                    {sortedDiscounts.map(discount => (
                      <CreditItem key={discount.id} discount={discount} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  );
};
